import {
  Card,
  CardContent,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography
} from '@mui/material';
import { TenantPage } from 'components/common';
import FileUploader from 'components/common/file-uploader';
import SectionTitle from 'components/common/SectionTitle';
import EntityHistory from 'components/widgets/EntityHistory';
import toast from 'features/toast';
import { IEquipment, IAccessor, IOption, IContract, IContractEquipment } from 'interfaces';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams } from 'react-router-dom';
import {
  equipmentService,
  equipmentHistoryLineService,
  contractEquipmentService
} from 'services/api';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EquipmentForm from '../EquipmentForm';
import EquipmentToCostDialog from '../EquipmentToCostDialog';
import ConfirmationUnlinkButtonDialog from 'components/helpers/ConfirmationButtonUnlink';
import { DocumentTableCardComponent } from 'components/common/documentLM/DocumentTableCardComponent';
import { isTechnicianCoordinator } from 'components/helpers/IsTechnicianCoordinator';
import { isDirector } from 'components/helpers/IsDirector';
import { isSupplier } from 'components/helpers/IsSupplier';
import { isTechnician } from 'components/helpers/IsTechnician';
import { HistoryLineList } from 'components/common/HistoryList/list/HisotryLineList';
import { EquipmentHistoryLineColumns } from 'components/common/enhanced/common-headers/equipment-history-lines';
import { DataGridPremium, GridColumns } from '@mui/x-data-grid-premium';
import { EquipmentAccessorColumns } from 'components/common/enhanced/common-headers/equipment-accessories';
import { baseCell } from 'components/common/enhanced/cells/base-cell';
import { booleanCell } from 'components/common/enhanced/cells/boolean-cell';
import { IsTenantAdministrator } from 'components/helpers/IsTenantAdministrator';

const downloadableFields = [
  'technical_file',
  'user_manual',
  'ce_ivd',
  'maintenance_checklist_template',
  'preemptive_maintenance_calendar'
];

export const EditEquipment = () => {
  const { t } = useTranslation();
  const translationPrefix = 'pages.equipment.edit';
  const { equipmentId } = useParams();

  const [equipment, setEquipment] = useState<IEquipment>();
  const [contractEquipments, setContractEquipments] = useState<IContractEquipment[]>([]);
  const [mode, setMode] = useState('');

  const isDisabledEdit =
    isTechnicianCoordinator() || isTechnician() || isDirector() || isSupplier();

  const fetchEquipment = () => {
    equipmentService.get(+equipmentId).then((res) => {
      setEquipment(res);
    });
  };

  useEffect(() => {
    fetchEquipment();
    contractEquipmentService
      .getAllPaginated(100, 0, { equipment_id: equipmentId, active: true })
      .then((res) => {
        setContractEquipments(res.results);
      });
  }, [equipmentId]);

  // -------------------------------------- UPDATE MANAGEMENT
  const updateEquipment = async (values) => {
    equipmentService
      .update(equipment.id, {
        ...values,
        market_value: values['market_value'] && values['market_value'].replace(',', '.')
      })
      .then(() => {
        toast.success('Strumento aggiornato con successo');
      });
  };
  // -------------------------------------- REMOVE COST LINK MANAGEMENT
  const handleRemoveLink = (id: number, pars: string) => {
    equipmentService.removeCostLink(+equipmentId, { model: pars, object_id: id }).then(() => {
      fetchEquipment();
      toast.success(t(translationPrefix + '.price-origin-removed-success'));
    });
  };

  const columns: GridColumns = EquipmentAccessorColumns();
  const contractColumns: GridColumns = [
    {
      field: 'contract_header_code',
      headerName: t('entities.contract-equipment.contract_code'),
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'maintenance_open',
      headerName: t('entities.contract-equipment.maintenance_open'),
      width: 200,
      type: 'boolean',
      renderCell: booleanCell(t)
    },
    {
      field: 'ticketing_open',
      headerName: t('entities.contract-equipment.ticketing_open'),
      width: 200,
      type: 'boolean',
      renderCell: booleanCell(t)
    }
  ];

  // -------------------------------------- HISTORY MANAGEMENT
  const historyRefresher = useRef(null);
  const handleDateTimeUpdate = (id: number, datetime: string) => {
    equipmentHistoryLineService.update(id, { datetime: datetime }).then(() => {
      toast.success(t('actions.successfully.updated'));
      if (historyRefresher.current) {
        historyRefresher.current(); // ✅ Assicura che venga chiamata solo se assegnata
      }
    });
  };

  const handleDeleteLine = (id: number) => {
    equipmentHistoryLineService.delete(id).then(() => {
      toast.success(t('actions.successfully.deleted'));
      if (historyRefresher.current) {
        historyRefresher.current(); // ✅ Assicura che venga chiamata solo se assegnata
      }
    });
  };

  const manualRefresh = () => {
    if (historyRefresher.current) {
      historyRefresher.current(); // ✅ Chiama la funzione di refresh se esiste
    }
  };

  const historyColumns = EquipmentHistoryLineColumns(handleDateTimeUpdate, handleDeleteLine, false);

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      entityCode={equipment?.code}>
      {equipment && (
        <div className="flex flex-wrap">
          <EquipmentForm
            equipment={equipment}
            onSubmit={updateEquipment}
            disableEdit={isDisabledEdit}
          />
          <IsTenantAdministrator>
            <div className="w-full mt-4">
              <SectionTitle>{t(`global.contracts`)}</SectionTitle>
              <Card variant="outlined">
                <CardContent>
                  <DataGridPremium
                    autoHeight
                    density="compact"
                    rows={contractEquipments}
                    columns={contractColumns}
                    sx={{ border: 'none' }}
                    disableSelectionOnClick
                  />
                </CardContent>
              </Card>
            </div>
          </IsTenantAdministrator>
          <div className="w-full mb-4 mt-4">
            <SectionTitle>{t(`entities.equipment.accessories`)}</SectionTitle>
            <Card variant="outlined">
              <CardContent>
                <DataGridPremium
                  autoHeight
                  density="compact"
                  rows={equipment.accessorList}
                  columns={columns}
                  sx={{ border: 'none' }}
                  disableSelectionOnClick
                />
              </CardContent>
            </Card>
          </div>

          <div className="py-4 w-full mb-4">
            <SectionTitle>{t(`global.attachments`)}</SectionTitle>
            <IsTenantAdministrator>
              <Card variant="outlined">
                <CardContent>
                  <div className="flex flex-wrap">
                    {downloadableFields.map((field, i) => (
                      <div key={i} className={`w-1/5`}>
                        <span>{t(`entities.equipment.${field}`)}:</span>
                        <FileUploader
                          service={equipmentService}
                          entityId={equipment.id}
                          attachmentName={field}
                          filename={equipment[field]}
                          disableEdit={isDisabledEdit}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </IsTenantAdministrator>
          </div>

          {/* <IsSuperAdmin> */}
          <div className="w-full p-4 flex flex-wrap">
            <div className="w-full">
              <SectionTitle>{t('global.costs')}</SectionTitle>
            </div>
            {equipment &&
              [
                { field: 'laboratories', link: '/tenant/topology/lab/' },
                { field: 'operationalunits', link: '/tenant/topology/operationalunit/' },
                { field: 'sectors', link: '/tenant/topology/sector/' },
                { field: 'workstations', link: '/tenant/cost-analysis/workstations/' },
                { field: 'exams', link: '/tenant/cost-analysis/exams/' }
              ].map((e) => (
                <div
                  key={e.field}
                  className="w-full sm:w-1/2 lg:w-1/5 pl-4 first:pl-0 border-r last:border-0">
                  <div className="flex justify-start items-center mt-4 mb-2">
                    <Typography variant="body1" component="div" fontWeight={700}>
                      {t(`global.${e.field}`)}
                    </Typography>
                    {!isDisabledEdit && (
                      <IconButton color="primary" onClick={() => setMode(e.field)}>
                        <AddCircleOutlineIcon />
                      </IconButton>
                    )}
                  </div>
                  <List dense={true} className="ml-0">
                    {equipment[e.field] &&
                      equipment[e.field].map((entity, i) => (
                        <ListItem
                          key={i}
                          sx={{ padding: 0 }}
                          secondaryAction={
                            <div key={i}>
                              <NavLink to={`${e.link}${entity.id}`}>
                                <IconButton edge="end">
                                  <VisibilityIcon />
                                </IconButton>
                              </NavLink>
                              <ConfirmationUnlinkButtonDialog
                                handleUnlink={handleRemoveLink}
                                id={entity.id}
                                pars={e.field}
                                iconButton={true}
                              />
                            </div>
                          }>
                          <ListItemButton>
                            <ListItemText primary={entity.code} secondary={entity['description']} />
                            <Divider variant="inset" component="li" />
                          </ListItemButton>
                        </ListItem>
                      ))}
                  </List>
                </div>
              ))}
          </div>
          {
            <EquipmentToCostDialog
              equipmentId={+equipmentId}
              mode={mode}
              setMode={setMode}
              callback={fetchEquipment}
            />
          }

          <div className="w-full p-4">
            <Card variant="outlined">
              <DocumentTableCardComponent
                originalService={'equipment'}
                objectId={equipment.id}
                dictParams={{ equipment: equipmentId }}
                folder={'menu.equipments/' + equipment.code}
                folder_description={`Descrizione: ${equipment.description}; Seriale: ${equipment.serial_number}; `}
                disableEdit={isDisabledEdit}
              />
            </Card>
          </div>

          {/* History component */}
          <div className="w-full py-2">
            <Card variant="outlined">
              <CardContent>
                <HistoryLineList
                  service={equipmentHistoryLineService}
                  columns={historyColumns}
                  outerParams={{ equipment_code: equipment.code }}
                  refresh={historyRefresher}
                  manualRefresh={manualRefresh}
                />
              </CardContent>
            </Card>
          </div>

          {/* History */}
          <div className="w-full md:w-1/2 pr-4">
            <Card variant="outlined">
              <CardContent>
                <EntityHistory service={equipmentService} entityId={equipment.id} />
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </TenantPage>
  );
};

export default EditEquipment;
